<template>
  <div
    v-if="item"
    class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
    @click.self="$emit('close')"
  >
    <div class="bg-white rounded-lg w-full max-w-6xl max-h-[92vh] overflow-hidden flex flex-col shadow-xl">
      <div class="p-4 border-b flex justify-between items-start gap-4 shrink-0">
        <div class="min-w-0">
          <p class="text-xs text-gray-400 uppercase">{{ item.format || 'pin' }} · {{ item.scheduled_for }}</p>
          <h3 class="font-semibold text-lg leading-snug">{{ item.title }}</h3>
          <p v-if="item.brief" class="text-sm text-gray-600 mt-1 line-clamp-2">{{ item.brief }}</p>
        </div>
        <button type="button" class="text-gray-500 text-2xl leading-none shrink-0" @click="$emit('close')">×</button>
      </div>

      <div class="px-4 border-b flex gap-1 shrink-0">
        <button
          type="button"
          class="px-4 py-2 text-sm border-b-2 -mb-px"
          :class="tab === 'content' ? 'border-gray-800 text-gray-800 font-medium' : 'border-transparent text-gray-500'"
          @click="tab = 'content'"
        >
          {{ $t('smm.schedule_modal.tab_content') }}
        </button>
        <button
          type="button"
          class="px-4 py-2 text-sm border-b-2 -mb-px"
          :class="tab === 'logs' ? 'border-gray-800 text-gray-800 font-medium' : 'border-transparent text-gray-500'"
          @click="tab = 'logs'"
        >
          {{ $t('smm.schedule_modal.tab_logs') }}
        </button>
      </div>

      <div class="flex-1 overflow-y-auto p-4">
        <div v-if="tab === 'content'" class="grid lg:grid-cols-2 gap-6">
          <div class="space-y-4">
            <div class="rounded-lg border p-4 space-y-3">
              <div class="flex items-start justify-between gap-2">
                <h4 class="text-sm font-medium">{{ $t('smm.schedule_modal.matched_content') }}</h4>
                <button
                  type="button"
                  class="text-xs text-gray-700 hover:underline shrink-0"
                  :disabled="resolving"
                  @click="resolveMedia(true)"
                >
                  {{ resolving ? $t('smm.schedule_modal.resolving') : $t('smm.schedule_modal.rematch') }}
                </button>
              </div>

              <div v-if="resolving" class="text-sm text-gray-500">{{ $t('smm.schedule_modal.searching') }}</div>

              <template v-else-if="match">
                <div class="flex items-center gap-2 flex-wrap">
                  <span class="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700 uppercase">{{ kindLabel(match.kind) }}</span>
                  <span v-if="match.match_type === 'fuzzy'" class="text-xs text-amber-700">{{ $t('smm.schedule_modal.match_fuzzy') }}</span>
                  <span v-else-if="match.match_type === 'exact'" class="text-xs text-green-700">{{ $t('smm.schedule_modal.match_exact') }}</span>
                  <span v-if="match.score" class="text-xs text-gray-400">{{ Math.round(match.score * 100) }}%</span>
                </div>
                <p class="font-medium text-gray-900">{{ match.title }}</p>

                <div v-if="match.description" class="text-sm text-gray-600 bg-gray-50 rounded p-2 max-h-32 overflow-y-auto">
                  <p class="text-xs text-gray-500 mb-1">{{ $t('smm.schedule_modal.library_description') }}</p>
                  {{ match.description }}
                </div>

                <div v-if="selectedPreviewFiles.length" class="space-y-2">
                  <div class="grid grid-cols-2 gap-2">
                    <div
                      v-for="file in selectedPreviewFiles"
                      :key="file.id"
                      class="rounded-lg overflow-hidden border bg-gray-50"
                    >
                      <img
                        :src="file.url"
                        :alt="file.original_name || match.title"
                        class="w-full max-h-40 object-contain bg-gray-100"
                      />
                    </div>
                  </div>
                </div>
                <div v-else-if="videoFile" class="rounded-lg overflow-hidden border bg-gray-50">
                  <div class="aspect-video bg-gray-900 flex items-center justify-center text-white text-sm">
                    {{ $t('smm.schedule_modal.video_label', { name: videoFile.original_name || $t('smm.media.mp4_badge') }) }}
                  </div>
                </div>
                <p v-else-if="!imageFiles.length" class="text-sm text-gray-500">{{ $t('smm.schedule_modal.no_images') }}</p>

                <div v-if="imageFiles.length" class="space-y-1">
                  <p class="text-xs text-gray-500">
                    {{ $t('smm.schedule_modal.photo_selection_hint', { n: selectedFileIds.length }) }}
                  </p>
                  <div class="flex flex-wrap gap-2">
                    <button
                      v-for="file in imageFiles"
                      :key="file.id"
                      type="button"
                      class="relative w-16 h-16 rounded border-2 overflow-hidden shrink-0"
                      :class="isFileSelected(file.id) ? 'border-blue-600 ring-2 ring-blue-200' : 'border-gray-200 hover:border-gray-400'"
                      @click="toggleFile(file.id)"
                    >
                      <img :src="file.url" class="w-full h-full object-cover" alt="" />
                    </button>
                  </div>
                </div>

                <div v-if="videoFiles.length" class="space-y-1">
                  <p class="text-xs text-gray-500">{{ $t('smm.schedule_modal.video_hint') }}</p>
                  <div class="flex flex-wrap gap-2">
                    <div
                      v-for="file in videoFiles"
                      :key="file.id"
                      class="w-16 h-16 rounded border bg-gray-800 flex items-center justify-center text-[10px] text-white"
                    >
                      {{ $t('smm.media.mp4_badge') }}
                    </div>
                  </div>
                </div>

                <div v-if="linkUrl">
                  <label class="text-xs text-gray-500">{{ $t('common.link') }}</label>
                  <a
                    :href="linkUrl"
                    target="_blank"
                    rel="noopener"
                    class="block text-sm text-gray-700 hover:underline break-all mt-0.5"
                  >
                    {{ linkUrl }}
                  </a>
                </div>
              </template>

              <p v-else class="text-sm text-gray-500">
                {{ $t('smm.schedule_modal.no_match') }}
              </p>
            </div>

            <div v-if="generatedImage" class="rounded-lg border p-4 space-y-3 border-green-200 bg-green-50/40">
              <h4 class="text-sm font-medium text-green-900">{{ $t('smm.schedule_modal.generated_pin') }}</h4>
              <img
                :src="generatedImage.url"
                :alt="item.payload?.alt_text || item.title"
                class="w-full max-h-80 object-contain rounded-lg border bg-white"
              />
              <div v-if="item.payload?.description" class="text-sm text-gray-700">
                <p class="text-xs text-gray-500 mb-1">{{ $t('common.description') }}</p>
                {{ item.payload.description }}
              </div>
              <p v-if="hashtagsText" class="text-xs text-gray-600">
                {{ hashtagsText }}
              </p>
            </div>

            <div v-if="item.payload && !generatedImage" class="bg-gray-50 rounded-lg p-3">
              <h4 class="text-sm font-medium mb-2">{{ $t('smm.schedule_modal.ai_json') }}</h4>
              <pre class="text-xs overflow-x-auto max-h-40">{{ JSON.stringify(item.payload, null, 2) }}</pre>
            </div>

            <div v-if="item.external_id" class="text-sm text-green-700 bg-green-50 p-2 rounded">
              {{ $t('smm.schedule_modal.published', { date: item.published_at }) }}
            </div>
            <div v-if="item.publish_error" class="text-sm text-red-600 bg-red-50 p-2 rounded">
              {{ item.publish_error }}
            </div>
          </div>

          <div class="space-y-4">
            <div>
              <label class="text-sm font-medium">{{ $t('smm.schedule_modal.prompt') }}</label>
              <select
                :value="generateForm.prompt_template_id"
                class="w-full border rounded-lg p-2 mt-1 text-sm"
                @change="$emit('update:generateForm', { ...generateForm, prompt_template_id: $event.target.value })"
              >
                <option value="">{{ $t('common.select_placeholder') }}</option>
                <option v-for="p in postPrompts" :key="p.id" :value="p.id">{{ p.name }}</option>
              </select>
            </div>

            <div>
              <label class="text-sm font-medium">{{ $t('smm.schedule_modal.extra_photos') }}</label>
              <p class="text-xs text-gray-500 mt-0.5">{{ $t('smm.schedule_modal.extra_photos_hint') }}</p>
              <input type="file" multiple accept="image/*" class="mt-1 text-sm w-full" @change="$emit('files', $event)" />
            </div>

            <div>
              <label class="text-sm font-medium">{{ $t('smm.schedule_modal.notes') }}</label>
              <textarea
                :value="generateForm.user_notes"
                rows="3"
                class="w-full border rounded-lg p-2 mt-1 text-sm"
                @input="$emit('update:generateForm', { ...generateForm, user_notes: $event.target.value })"
              />
            </div>

            <div v-if="isPinterest" class="border rounded-lg p-4 space-y-2">
              <h4 class="text-sm font-medium">{{ $t('smm.schedule_modal.pinterest') }}</h4>
              <input
                :value="publishForm.link_url"
                type="url"
                :placeholder="$t('smm.schedule_modal.link_placeholder')"
                class="w-full border rounded-lg p-2 text-sm"
                @input="$emit('update:publishForm', { ...publishForm, link_url: $event.target.value })"
              />
              <select
                :value="publishForm.board_id"
                class="w-full border rounded-lg p-2 text-sm"
                @change="$emit('update:publishForm', { ...publishForm, board_id: $event.target.value })"
              >
                <option value="">{{ $t('smm.schedule_modal.default_board') }}</option>
                <option v-for="b in pinterestBoards" :key="b.id" :value="b.id">{{ b.name }}</option>
              </select>
              <button
                type="button"
                class="w-full text-sm bg-red-600 text-white py-2 rounded-lg disabled:opacity-50"
                :disabled="publishing"
                @click="$emit('publish')"
              >
                {{ publishing ? $t('smm.schedule_modal.publishing') : $t('smm.schedule_modal.publish') }}
              </button>
            </div>
          </div>
        </div>

        <div v-else>
          <ai-generations-panel :content-item-id="item.id" type="post_content" />
        </div>
      </div>

      <div v-if="tab === 'content'" class="p-4 border-t flex justify-end gap-2 shrink-0">
        <button type="button" class="px-4 py-2 text-gray-600" @click="$emit('close')">{{ $t('common.close') }}</button>
        <button
          type="button"
          class="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50"
          :disabled="!generateForm.prompt_template_id || generating"
          @click="$emit('generate')"
        >
          {{ generating ? $t('common.generating', { label: aiProviderLabel }) : $t('smm.schedule_modal.generate') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import AiGenerationsPanel from '@/components/smm/AiGenerationsPanel.vue'

export default {
  name: 'ScheduleItemModal',
  components: { AiGenerationsPanel },
  props: {
    item: { type: Object, default: null },
    match: { type: Object, default: null },
    postPrompts: { type: Array, default: () => [] },
    generateForm: { type: Object, required: true },
    publishForm: { type: Object, required: true },
    pinterestBoards: { type: Array, default: () => [] },
    generating: { type: Boolean, default: false },
    publishing: { type: Boolean, default: false },
    aiProviderLabel: { type: String, default: 'AI' },
    isPinterest: { type: Boolean, default: false },
    linkUrl: { type: String, default: '' },
    resolving: { type: Boolean, default: false },
  },
  emits: ['close', 'generate', 'publish', 'files', 'update:generateForm', 'update:publishForm', 'resolve', 'toggle-file'],
  data: () => ({
    tab: 'content',
  }),
  computed: {
    selectedFileIds() {
      const ids = this.match?.selected_file_ids
      if (Array.isArray(ids) && ids.length) return ids
      if (this.match?.selected_file_id) return [this.match.selected_file_id]
      return []
    },
    imageFiles() {
      return (this.match?.files || []).filter((f) => f.type === 'image')
    },
    videoFiles() {
      return (this.match?.files || []).filter((f) => f.type === 'video')
    },
    selectedPreviewFiles() {
      if (!this.imageFiles.length || !this.selectedFileIds.length) return []
      const ids = this.selectedFileIds.map((id) => Number(id))
      return this.imageFiles.filter((f) => ids.includes(Number(f.id)))
    },
    videoFile() {
      return this.videoFiles[0] || null
    },
    generatedImage() {
      const attachments = (this.item?.media || []).filter((m) => m.purpose === 'attachment')
      return attachments.length ? attachments[attachments.length - 1] : null
    },
    hashtagsText() {
      const tags = this.item?.payload?.hashtags
      if (!tags) return ''
      if (Array.isArray(tags)) return tags.filter(Boolean).join(' ')
      return String(tags)
    },
  },
  watch: {
    item() {
      this.tab = 'content'
    },
  },
  methods: {
    kindLabel(kind) {
      if (kind === 'video') return this.$t('smm.content_kind.video')
      if (kind === 'digital masterclass') return this.$t('smm.content_kind.masterclass')
      return this.$t('smm.content_kind.content')
    },
    resolveMedia(force = false) {
      this.$emit('resolve', force)
    },
    toggleFile(fileId) {
      this.$emit('toggle-file', fileId)
    },
    isFileSelected(fileId) {
      const id = Number(fileId)
      return this.selectedFileIds.some((selectedId) => Number(selectedId) === id)
    },
  },
}
</script>
